import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/providers/auth-provider'
import { useRegister } from '@/hooks/use-auth-api'
import { RegisterForm as RegisterFormType } from '@/types'
import { OAuthButtons } from '@/components/auth/oauth-buttons'
import { useRecaptcha, extractRateLimitError, isRecaptchaError } from '@/hooks/use-recaptcha'

const registerSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirm_password: z.string(),
  first_name: z.string().min(2, 'First name must be at least 2 characters').optional(),
  last_name: z.string().min(2, 'Last name must be at least 2 characters').optional(),
  gdpr_consent: z.literal(true, { errorMap: () => ({ message: 'You must agree to continue' }) }),
}).refine((data) => data.password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
})

export function RegisterPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const registerMutation = useRegister()
  const { executeRecaptcha, isEnabled: recaptchaEnabled } = useRecaptcha({ action: 'register' })

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormType>({
    resolver: zodResolver(registerSchema),
  })

  const onSubmit = async (data: RegisterFormType) => {
    try {
      // Generate reCAPTCHA token if enabled
      let recaptcha_token: string | null = null
      if (recaptchaEnabled) {
        recaptcha_token = await executeRecaptcha()
      }

      const result = await registerMutation.mutateAsync({
        ...data,
        recaptcha_token,
      } as RegisterFormType & { recaptcha_token: string | null })
      toast.success(result.message || 'Account created successfully!')

      // Show email verification message if needed
      if (result.message?.includes('verification')) {
        toast.success('Please check your email to verify your account', {
          duration: 6000,
        })
      }

      navigate('/login', {
        state: {
          message: 'Account created! Please sign in to continue.'
        }
      })
    } catch (error: any) {
      // Handle rate limit errors
      const rateLimitError = extractRateLimitError(error)
      if (rateLimitError.isRateLimited) {
        toast.error(rateLimitError.message, { duration: 6000 })
        return
      }

      // Handle reCAPTCHA verification errors
      if (isRecaptchaError(error)) {
        toast.error('Security check failed. Please try again.', { duration: 5000 })
        return
      }

      toast.error(error.message || 'Registration failed')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Create your account
          </CardTitle>
          <CardDescription className="text-center">
            Start building beautiful waitlists today
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="first_name" className="text-sm font-medium">
                  First name
                </label>
                <Input
                  id="first_name"
                  placeholder="John"
                  autoComplete="given-name"
                  error={errors.first_name?.message}
                  {...register('first_name')}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="last_name" className="text-sm font-medium">
                  Last name
                </label>
                <Input
                  id="last_name"
                  placeholder="Doe"
                  autoComplete="family-name"
                  error={errors.last_name?.message}
                  {...register('last_name')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                error={errors.email?.message}
                {...register('email')}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <Input
                id="password"
                type="password"
                placeholder="Create a strong password"
                autoComplete="new-password"
                error={errors.password?.message}
                {...register('password')}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="confirm_password" className="text-sm font-medium">
                Confirm password
              </label>
              <Input
                id="confirm_password"
                type="password"
                placeholder="Confirm your password"
                autoComplete="new-password"
                error={errors.confirm_password?.message}
                {...register('confirm_password')}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="gdpr_consent"
                  checked={watch('gdpr_consent') === true}
                  onCheckedChange={(checked) => setValue('gdpr_consent', checked === true, { shouldValidate: true })}
                />
                <label htmlFor="gdpr_consent" className="text-sm leading-tight cursor-pointer">
                  I agree to the{' '}
                  <Link to="/privacy" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/terms" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                    Terms of Service
                  </Link>
                </label>
              </div>
              {errors.gdpr_consent && (
                <p className="text-sm text-destructive">{errors.gdpr_consent.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={registerMutation.isPending}
            >
              Create Account
            </Button>
          </form>

          {/* OAuth signup buttons */}
          <div className="mt-6">
            <OAuthButtons mode="register" />
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  Already have an account?
                </span>
              </div>
            </div>
            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm text-primary hover:underline"
              >
                Sign in instead
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
